import axios from 'axios';
import i18next from 'i18next';
import { Menu } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ArrowPathIcon, ChevronDownIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import Footer from '../header_footer/Footer';
import Navigation from '../header_footer/Navgation';
import ProductCard from '../main_compos/ProductCard';

const FilterDropdown = ({ title, options, selectedValue, onChange }) => {
  const { t } = useTranslation();
  return (
    <Menu as="div" className="relative text-sm">
      <Menu.Button className="w-full px-2 py-1.5 text-left border rounded-md flex items-center justify-between">
        {t(selectedValue) || t(title)}
        <ChevronDownIcon className="h-4 w-4" />
      </Menu.Button>
      <Menu.Items className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-lg">
        <div className="p-2">
          {options.map((option) => (
            <Menu.Item key={option.value}>
              {({ active }) => (
                <button onClick={() => onChange(option.value)} className={`${active ? 'bg-gray-100' : ''} w-full text-left px-2 py-1 rounded-md`} >
                  {t(option.label)}
                </button>
              )}
            </Menu.Item>
          ))}
        </div>
      </Menu.Items>
    </Menu>
  )
};

export default function Shop() {
  const { t } = useTranslation();
  const { gender } = useParams();
  const location = useLocation();
  const localhost = process.env.REACT_APP_LOCALHOST;
  const [loading, setLoading] = useState(true);
  const [filterProds, setFilterProds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    gender: gender || 'all',
    stock: 'all',
    sort: 'newest'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${localhost}all-categories`);
        setCategories(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchCategories();
  }, []);

  // Parse URL query parameters for filters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const categoryParam = queryParams.get('category');
    const genderParam = queryParams.get('gender');
    const stockParam = queryParams.get('stock');
    const sortParam = queryParams.get('sort');

    setFilters(prev => ({
      ...prev,
      category: categoryParam || '',
      gender: genderParam || 'all',
      stock: stockParam || 'all',
      sort: sortParam || 'newest',
    }));

    // Reset pagination when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [location.search]);

  // Fetch products based on filters and pagination
  const fetchProducts = async (page = 1) => {
    try {
      let url = `${localhost}all-products?page=${page}`;

      // Add filters to URL
      if (filters.category) url += `&category=${filters.category}`;
      if (filters.gender !== 'all') url += `&gender=${filters.gender}`;
      if (filters.stock !== 'all') url += `&stock=${filters.stock}`;
      if (filters.sort) url += `&sort=${filters.sort}`;

      const res = await axios.get(url);
      const alldata = res.data.data;
      const updatedData = alldata.map(item => {
        const category = item.category ? JSON.parse(item.category) : null;
        const imgs = item.imgs ? JSON.parse(item.imgs) : null;
        return { ...item, category, imgs };
      });
      setFilterProds(updatedData);
      setPagination({
        currentPage: res.data.current_page,
        totalPages: res.data.last_page,
      });
    } catch (err) {
      console.error(err);
    }finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const handleFilterChange = (type, value) => {
    setLoading(true); // Set loading to true when a filter changes
    setFilters(prev => ({ ...prev, [type]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  
    // Update the URL with the new query parameter
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set(type, value);
    window.history.replaceState(null, '', `${location.pathname}?${newSearchParams.toString()}`);
  };

  const handlePagination = (page) => {
    setLoading(true); // Set loading to true when pagination changes
    setPagination(prev => ({ ...prev, currentPage: page }));
    fetchProducts(page);
  };

  const reset = () => {
    setFilters({
      category: '',
      gender: gender || 'all',
      stock: 'all',
      sort: 'newest'
    });
    setPagination({
      currentPage: 1,
      totalPages: 1,
    });

    // Reset URL query parameters
    window.history.replaceState(null, '', '/shop');
    fetchProducts(1);
  };

  // Define filter options
  const sortOptions = [
    { value: 'name_asc', label: 'name-a-z' },
    { value: 'name_desc', label: 'name-z-a' },
    { value: 'price_asc', label: 'price-low-high' },
    { value: 'price_desc', label: 'price-high-low' },
    { value: 'newest', label: 'newest' },
    { value: 'oldest', label: 'oldest' },
  ];

  const stockOptions = [
    { value: 'all', label: 'all-products' },
    { value: 'available', label: 'in-stock' },
    { value: 'not_available', label: 'out-of-stock' },
  ];

  const genderOptions = [
    { value: 'all', label: 'all' },
    { value: 'men', label: 'men' },
    { value: 'women', label: 'women' },
  ];

  const categoryOptions = [
    { value: '', label: 'all-categories' },
    ...categories.map(cat => ({
      value: cat.id,
      label: cat.name[i18next.language]
    }))
  ];

  return (
    <section>
      <Navigation />
      <main className="container mx-auto pt-10 sm:pt-16 p-4">
        <div className="px-4 py-2 border shadow-sm rounded-lg max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">{t("all-our-products")}</h2>
            <button onClick={reset} className="p-2 hover:bg-gray-100 text-black rounded-full">
              <ArrowPathIcon className="size-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 sm:grid-cols-2 gap-4 mb-4">
            <FilterDropdown
              title={t("category")}
              options={categoryOptions}
              selectedValue={categoryOptions.find(opt => opt.value === filters.category)?.label}
              onChange={(value) => handleFilterChange('category', value)}
            />
            <FilterDropdown
              title={t("gender")}
              options={genderOptions}
              selectedValue={genderOptions.find(opt => opt.value === filters.gender)?.label}
              onChange={(value) => handleFilterChange('gender', value)}
            />
            <FilterDropdown
              title={t("availability")}
              options={stockOptions}
              selectedValue={stockOptions.find(opt => opt.value === filters.stock)?.label}
              onChange={(value) => handleFilterChange('stock', value)}
            />
            <FilterDropdown
              title={t("sort")}
              options={sortOptions}
              selectedValue={sortOptions.find(opt => opt.value === filters.sort)?.label}
              onChange={(value) => handleFilterChange('sort', value)}
            />
          </div>
        </div>

        <div className="my-14">
          {loading ? (
            <div className="min-h-[55vh] flex justify-center items-center">
              <div className="loading-spinner" />
            </div>
          ) : filterProds.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4 md:gap-8 lg:grid-cols-4">
              {filterProds.map((item, index) => (
                <ProductCard key={index} item={item} />
              ))}
            </div>
          ) : (
            <div className="text-center flex items-center justify-center min-h-[55vh]">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("no-found-title")}</h3>
                <p className="text-gray-600">{t("no-found-text")}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 my-8">
          <button
            onClick={() => handlePagination(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="inline-flex items-center text-sm font-medium transition-colors rounded-sm
              disabled:opacity-30 disabled:cursor-not-allowed
              bg-white text-black/80
              hover:bg-gray-50 hover:text-black
              focus:outline-none focus:ring-1 focus:ring-orange-500 focus:ring-offset-2"
          >
            <ChevronLeftIcon className="size-6" />
          </button>

          <div className="flex items-center text-sm font-medium text-black/80">
            <span className="px-3 py-1 rounded-lg bg-black/5">
              {pagination.currentPage}
            </span>
            <span className="mx-2">{t("of")}</span>
            <span className="px-3 py-1 rounded-lg bg-black/5">
              {pagination.totalPages}
            </span>
          </div>

          <button
            onClick={() => handlePagination(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="inline-flex items-center text-sm font-medium transition-colors rounded-sm
              disabled:opacity-30 disabled:cursor-not-allowed
              bg-white text-black/80
              hover:bg-gray-50 hover:text-black
              focus:outline-none focus:ring-1 focus:ring-orange-500 focus:ring-offset-2"
          >
            <ChevronRightIcon className="size-6" />
          </button>
        </div>
      </main>
      <Footer />
    </section>
  );
}